import React, { useState } from "react";
import { Link } from "react-router-dom";
import { del, patch, post } from "../lib/api";
import { addDays, formatDay } from "../lib/format";
import { todayISO } from "../../helpers/datetime";
import { Button, Card, Checkbox, Empty, Field, Input } from "../ui";
import { HiddenLabel } from "./Hidden";
import EntityPicker from "./EntityPicker";
import { SourceBadge } from "./DetailParts";
import { useShell } from "../shell/context";

const period = (r) => {
    const start = r.start_date ? formatDay(r.start_date) : "";
    const end = r.is_current ? "now" : r.end_date ? formatDay(r.end_date) : "";
    if (!start && !end) return "";
    if (!start && r.is_current) return "Current";
    if (!start) return `Until ${end}`;
    return `${start || "?"} – ${end || "?"}`;
};

function CloseRole({ role, onDone, onCancel }) {
    const [date, setDate] = useState(addDays(todayISO(), -1));
    return (
        <div className="hz-row" style={{ gap: 6, marginTop: 6 }}>
            <span className="hz-small hz-muted">Ended on</span>
            <Input small type="date" value={date} min={role.start_date || undefined} onChange={(e) => setDate(e.target.value)} style={{ width: 150 }} />
            <Button size="sm" variant="primary" disabled={!date} onClick={() => onDone(date)}>Close role</Button>
            <Button size="sm" variant="light" onClick={onCancel}>Cancel</Button>
        </div>
    );
}

function AddRole({ contactId, hasCurrent, onDone, onCancel }) {
    const { toast } = useShell();
    const [company, setCompany] = useState(null);
    const [title, setTitle] = useState("");
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [closePrev, setClosePrev] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const save = async () => {
        if (!company) return setError("Pick a company.");
        setBusy(true);
        setError(null);
        try {
            const body = { company_id: company.id, title: title.trim() };
            if (start) body.start_date = start;
            if (end) body.end_date = end;
            if (!end && hasCurrent && closePrev) body.close_previous = true;
            const res = await post(`v1/crm/contacts/${contactId}/roles`, body);
            const closed = (res.closed || []).length;
            toast(closed ? `Role added; ${closed} previous ${closed === 1 ? "role" : "roles"} closed.` : "Role added.");
            onDone();
        } catch (e) {
            setError(e.message);
            setBusy(false);
        }
    };
    return (
        <div className="hz-card-section" style={{ background: "var(--hz-surface-subtle)" }}>
            <div className="hz-form-grid" style={{ gap: 10 }}>
                {error && <div className="hz-alert hz-alert-danger">{error}</div>}
                <div>
                    <span className="hz-label">Company</span>
                    <EntityPicker kind="company" value={company} onChange={setCompany} autoFocus />
                </div>
                <Field label="Title">
                    <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="CTO" />
                </Field>
                <div className="hz-form-2">
                    <Field label="Start">
                        <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
                    </Field>
                    <Field label="End" hint="Empty for a current role">
                        <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
                    </Field>
                </div>
                {hasCurrent && !end && (
                    <Checkbox checked={closePrev} onChange={setClosePrev}>Close the current roles (a job change)</Checkbox>
                )}
                <div className="hz-row">
                    <Button variant="primary" size="sm" disabled={busy} onClick={save}>Add role</Button>
                    <Button variant="light" size="sm" onClick={onCancel}>Cancel</Button>
                </div>
            </div>
        </div>
    );
}

// Roles with history: current first, then past. Manual roles are marked;
// imports never overwrite them.
export default function Roles({ contactId, roles, onChanged }) {
    const { toast } = useShell();
    const [adding, setAdding] = useState(false);
    const [closing, setClosing] = useState(null);
    const current = roles.filter((r) => r.is_current);
    const past = roles.filter((r) => !r.is_current);

    const closeRole = async (role, date) => {
        try {
            await patch(`v1/crm/roles/${role.id}`, { end_date: date });
            toast("Role closed.");
            setClosing(null);
            onChanged();
        } catch (e) {
            toast(e.message, { error: true });
        }
    };
    const removeRole = async (role) => {
        try {
            await del(`v1/crm/roles/${role.id}`);
            toast("Role moved to the trash.", {
                icon: "ri-delete-bin-line",
                action: { label: "Undo", run: () => post(`v1/crm/trash/roles/${role.id}/restore`).then(onChanged).catch((e) => toast(e.message, { error: true })) },
            });
            onChanged();
        } catch (e) {
            toast(e.message, { error: true });
        }
    };

    const row = (r) => (
        <div key={r.id} className="hz-list-item" style={{ alignItems: "flex-start" }}>
            <i className={r.is_current ? "ri-briefcase-4-line" : "ri-history-line"} style={{ fontSize: 16, color: r.is_current ? "var(--hz-primary)" : "var(--hz-gray-500)", marginTop: 2 }}></i>
            <div className="hz-spacer" style={{ minWidth: 0 }}>
                <div className="hz-row-wrap" style={{ gap: 6 }}>
                    <span className="hz-strong">{r.title || "No title"}</span>
                    <span className="hz-muted">at</span>
                    <Link to={`/crm/companies/${r.company_id}`}>{r.company_name}</Link>
                    <HiddenLabel at={r.company_hidden_at} />
                    <SourceBadge source={r.source} />
                </div>
                <div className="hz-small hz-muted">{period(r)}</div>
                {closing === r.id && <CloseRole role={r} onDone={(d) => closeRole(r, d)} onCancel={() => setClosing(null)} />}
            </div>
            {closing !== r.id && (
                <div className="hz-row" style={{ gap: 4 }}>
                    {r.is_current && <Button size="sm" variant="ghost" onClick={() => setClosing(r.id)}>Close</Button>}
                    <Button size="sm" variant="ghost" iconOnly icon="ri-delete-bin-line" title="Delete role" onClick={() => removeRole(r)} />
                </div>
            )}
        </div>
    );

    return (
        <Card noBody title="Roles" actions={!adding && <Button size="sm" variant="soft" icon="ri-add-line" onClick={() => setAdding(true)}>Add role</Button>}>
            {adding && (
                <AddRole
                    contactId={contactId}
                    hasCurrent={current.length > 0}
                    onCancel={() => setAdding(false)}
                    onDone={() => {
                        setAdding(false);
                        onChanged();
                    }}
                />
            )}
            {roles.length === 0 && !adding && <Empty small>No roles yet.</Empty>}
            {current.map(row)}
            {past.length > 0 && <div className="hz-tl-month" style={{ padding: "12px 16px 4px" }}>Past roles</div>}
            {past.map(row)}
        </Card>
    );
}
